import React, { useMemo, useState } from 'react'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { CursorPagination, DataGrid } from '@divvy-web/skylab.datagrid'

import { getColumnConfiguration } from './columns'
import DashboardDetailsDrawer from './DashboardDetailsDrawer'
import { useGetCreditApplications } from './GetCreditApplications.gql'
import styles from './styles.module.scss'

// We have to do this otherwise our tests will not work. We need to be able to turn the
// virtualization off to actually find rows in the table in our tests
// https://github.com/TanStack/virtual/issues/641
const CreditApplicationsDataGrid = ({ virtualize = true }: { virtualize?: boolean }) => {
  const [getClassName] = useNameFormatter('CreditApplicationsDataGrid')
  const { data, loading } = useGetCreditApplications()

  const mutableData = [...(data?.creditApplications ?? [])]
  const [rowClickedApplicationDetails, setRowClickedApplicationDetails] = useState<string | null>(null)

  const columns = useMemo(
    () =>
      getColumnConfiguration({
        onViewDetailsClick: (newSalesforceCreditId: string) => setRowClickedApplicationDetails(newSalesforceCreditId),
      }),
    [],
  )

  return (
    <div className={`${styles[getClassName('')]} fs-unmask`}>
      <div className={styles[getClassName('title')]}>
        <FormattedMessage
          defaultMessage='Manage your applications'
          id='sputnik.CreditApplicationsDataGrid__Lecg3+'
        />
      </div>
      <div className={styles[getClassName('datagrid')]}>
        <DataGrid
          columns={columns}
          data={mutableData}
          getRowId={(row) => row.salesforceCreditId}
          loading={loading}
          renderPagination={({ table, virtualizer }) => {
            return (
              <CursorPagination
                loading={loading}
                messages={{
                  loading: (
                    <FormattedMessage
                      defaultMessage='Loading applications'
                      id='sputnik.CreditApplicationsDataGrid__oPhRVc'
                    />
                  ),
                  loadMoreButtonLabel: (
                    <FormattedMessage
                      defaultMessage='Load more applications'
                      id='sputnik.CreditApplicationsDataGrid__QKsw8E'
                    />
                  ),
                  allLoaded: (
                    <FormattedMessage
                      defaultMessage='All records loaded'
                      id='sputnik.CreditApplicationsDataGrid__0F8UYj'
                    />
                  ),
                  totalCount: (totalCount) => (
                    <FormattedMessage
                      defaultMessage='of {totalCount} {totalCount, plural, one {Application} other {Applications}}'
                      id='sputnik.CreditApplicationsDataGrid__7gVLNE'
                      values={{ totalCount }}
                    />
                  ),
                  noItems: (
                    <FormattedMessage
                      defaultMessage='0 Applications'
                      id='sputnik.CreditApplicationsDataGrid__1dybRu'
                    />
                  ),
                }}
                table={table}
                virtualizer={virtualizer}
              />
            )
          }}
          virtualize={virtualize}
          onRowClick={(rowId) => setRowClickedApplicationDetails(rowId as string)}
        />
        <DashboardDetailsDrawer
          isShowing={rowClickedApplicationDetails !== null}
          salesforceCreditId={rowClickedApplicationDetails ?? ''}
          onClose={() => setRowClickedApplicationDetails(null)}
        />
      </div>
    </div>
  )
}

export default CreditApplicationsDataGrid
